import type { AppReasoningStyle } from "@/lib/appPreferences";
import type { DeviceContextSnapshot } from "@/lib/deviceContext";
import { fetchWithAuth, uploadWithAuthProgress } from "@/lib/fetchWithAuth";
import { unwrapApiData } from "@/lib/apiResponse";
import { isPublicSessionId } from "@/lib/agentHub";

export type AgentAttachmentKind = "site_photo" | "source_document";

export type AgentAttachmentDescriptor = {
  fileId: string;
  kind: AgentAttachmentKind;
};

export type AgentFocusTarget = {
  originalFileId: string;
  focusFileId: string;
  label?: string;
  selectionMethod: "point" | "lasso" | "text";
  textPrompt?: string;
  score: number;
  boxXYXY: [number, number, number, number];
  imageWidth: number;
  imageHeight: number;
};

export type AgentSiteEvidence = {
  frequencyBand?:
    "DAILY" | "MONTHLY" | "YEARLY" | "THREE_YEARLY" | "TEN_YEARLY";
  severityBand?:
    "NO_LOST_TIME" | "LOST_TIME_LT_30_DAYS" | "LOST_TIME_GTE_30_DAYS" | "FATAL";
  observationDays?: number;
  accidentCount?: number;
  nearMissCount?: number;
  maxLostWorkDays?: number;
  fatalityOccurred?: boolean;
};

export type AgentModelMode = "fast" | "pro";
export type AgentSessionSurface = "chat" | "risk_assessment_workspace";

export type AgentRunPayload = {
  message: string;
  sessionId?: string;
  attachments?: AgentAttachmentDescriptor[];
  attachmentIds?: string[];
  siteEvidence?: AgentSiteEvidence;
  focusTarget?: AgentFocusTarget;
  context: {
    userLanguage: string;
    modelMode: AgentModelMode;
    protocol: "v2";
    reasoningStyle: AppReasoningStyle;
    deviceContext?: DeviceContextSnapshot;
    riskAssessmentMethod?: string;
  };
  clientRequestId: string;
};

export type TbmAgentRunPayload = {
  message?: string;
  stage: "scenario" | "record" | "pair";
  sessionId: string;
  sourceRiskArtifactId?: string;
  sourceTbmScenarioArtifactId?: string;
  context: {
    userLanguage: string;
    modelMode: AgentModelMode;
    protocol: "v2";
    reasoningStyle: AppReasoningStyle;
    deviceContext?: DeviceContextSnapshot;
  };
  clientRequestId: string;
};

export type UploadedAgentAttachment = {
  fileId: string;
  readiness?: string;
  format?: string;
  characterCount?: number;
};

export type AgentAttachmentUploadProgress = {
  loaded: number;
  total: number;
  percent: number;
};

export type AgentSessionListItem = {
  id: string;
  agent_id: string;
  preferred_agent_id?: string | null;
  surface?: AgentSessionSurface;
  title: string;
  is_starred?: boolean;
  project_id?: string | null;
  last_message_at?: string | null;
  created_at?: string;
};

export type AgentSessionMessage = {
  messageId?: number | string;
  id?: number | string;
  role: string;
  content: string;
  createdAt?: string;
  created_at?: string;
  metadata?: Record<string, unknown>;
};

const apiUrl = () => process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";

const apiErrorMessage = (payload: unknown, fallback: string) => {
  if (!payload || typeof payload !== "object") return fallback;
  const record = payload as Record<string, unknown>;
  const nested =
    record.data && typeof record.data === "object"
      ? (record.data as Record<string, unknown>)
      : undefined;
  const error =
    record.error && typeof record.error === "object"
      ? (record.error as Record<string, unknown>)
      : undefined;
  const message = record.message ?? error?.message ?? nested?.message;
  return typeof message === "string" && message.trim() ? message : fallback;
};

export function buildAgentRunPayload(input: {
  message: string;
  sessionId?: string | null;
  attachments?: AgentAttachmentDescriptor[];
  siteEvidence?: AgentSiteEvidence;
  focusTarget?: AgentFocusTarget;
  userLanguage: string;
  modelMode: AgentModelMode;
  reasoningStyle: AppReasoningStyle;
  deviceContext?: DeviceContextSnapshot;
  riskAssessmentMethod?: string;
  clientRequestId?: string;
}): AgentRunPayload {
  const sitePhotoIds = (input.attachments ?? [])
    .filter((attachment) => attachment.kind === "site_photo")
    .map((attachment) => attachment.fileId);
  return {
    message: input.message,
    sessionId: input.sessionId || undefined,
    attachments: input.attachments?.length ? input.attachments : undefined,
    attachmentIds: sitePhotoIds.length ? sitePhotoIds : undefined,
    siteEvidence: input.siteEvidence,
    focusTarget: input.focusTarget,
    context: {
      userLanguage: input.userLanguage,
      modelMode: input.modelMode,
      protocol: "v2",
      reasoningStyle: input.reasoningStyle,
      deviceContext: input.deviceContext,
      riskAssessmentMethod: input.riskAssessmentMethod,
    },
    clientRequestId: input.clientRequestId ?? crypto.randomUUID(),
  };
}

export async function uploadAgentAttachment(
  file: File,
  kind: AgentAttachmentKind,
  signal?: AbortSignal,
  onProgress?: (progress: AgentAttachmentUploadProgress) => void,
  deferExtraction = false,
): Promise<UploadedAgentAttachment> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("attachmentKind", kind);
  if (kind === "source_document" && deferExtraction) {
    formData.append("deferExtraction", "true");
  }
  const response = onProgress
    ? await uploadWithAuthProgress(`${apiUrl()}/api/files`, formData, {
        signal,
        skipRedirectOn401: false,
        onProgress: (progress) => {
          const loaded =
            progress.total > 0
              ? Math.min(
                  file.size,
                  Math.round((progress.loaded / progress.total) * file.size),
                )
              : progress.percent >= 100
                ? file.size
                : 0;
          onProgress({ loaded, total: file.size, percent: progress.percent });
        },
      })
    : await fetchWithAuth(`${apiUrl()}/api/files`, {
        method: "POST",
        body: formData,
        signal,
        skipRedirectOn401: false,
      });
  const payload = await response.json().catch(() => null);
  if (response.status === 413) {
    throw new Error(
      apiErrorMessage(
        payload,
        "파일 용량이 서버의 업로드 제한을 초과했습니다.",
      ),
    );
  }
  if (!response.ok)
    throw new Error(
      apiErrorMessage(payload, `업로드 실패 (${response.status})`),
    );
  const unwrapped = unwrapApiData<unknown>(payload);
  if (!unwrapped || typeof unwrapped !== "object")
    throw new Error("파일 응답을 확인할 수 없습니다.");
  const root = unwrapped as Record<string, unknown>;
  const source =
    root.file && typeof root.file === "object"
      ? (root.file as Record<string, unknown>)
      : root;
  const extraction =
    source.extraction && typeof source.extraction === "object"
      ? (source.extraction as Record<string, unknown>)
      : root.extraction && typeof root.extraction === "object"
        ? (root.extraction as Record<string, unknown>)
        : {};
  const rawFileId = source.fileId ?? source.id ?? root.fileId ?? root.id;
  if (typeof rawFileId !== "string" && typeof rawFileId !== "number")
    throw new Error("파일 ID가 응답에 없습니다.");
  return {
    fileId: String(rawFileId),
    readiness:
      typeof (
        source.readiness ??
        root.readiness ??
        source.status ??
        root.status
      ) === "string"
        ? String(
            source.readiness ?? root.readiness ?? source.status ?? root.status,
          )
        : undefined,
    format:
      typeof source.format === "string"
        ? source.format
        : typeof extraction.format === "string"
          ? extraction.format
          : undefined,
    characterCount:
      typeof source.characterCount === "number"
        ? source.characterCount
        : typeof extraction.characterCount === "number"
          ? extraction.characterCount
          : undefined,
  };
}

export async function createAgentSession(input: {
  agentId: string;
  projectId?: string | number | null;
  title?: string;
  surface?: AgentSessionSurface;
  preferredAgentId?: string | null;
}): Promise<string> {
  const response = await fetchWithAuth(`${apiUrl()}/api/chat/sessions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      agentId: input.agentId,
      projectId: input.projectId || undefined,
      title: input.title,
      surface: input.surface,
      preferredAgentId: input.preferredAgentId,
    }),
    skipRedirectOn401: false,
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok)
    throw new Error(
      apiErrorMessage(payload, `세션 생성 실패 (${response.status})`),
    );
  const data = unwrapApiData<unknown>(payload);
  const record =
    data && typeof data === "object" ? (data as Record<string, unknown>) : {};
  const nested =
    record.session && typeof record.session === "object"
      ? (record.session as Record<string, unknown>)
      : record;
  const id = nested.id;
  if (!isPublicSessionId(id))
    throw new Error("세션 생성 응답에 유효한 공개 ID가 없습니다.");
  return id;
}

export async function createAgentRun(
  agentId: string,
  payload: AgentRunPayload,
  signal?: AbortSignal,
) {
  const response = await fetchWithAuth(
    `${apiUrl()}/api/agents/${agentId}/runs`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal,
      skipRedirectOn401: false,
    },
  );
  const body = await response.json().catch(() => null);
  if (!response.ok)
    throw new Error(apiErrorMessage(body, `서버 오류 (${response.status})`));
  const data = unwrapApiData<unknown>(body);
  const runId =
    data && typeof data === "object"
      ? (data as Record<string, unknown>).runId
      : undefined;
  if (typeof runId !== "string" || !runId)
    throw new Error("Core returned an invalid agent run response");
  return runId;
}

export async function createTbmAgentRun(
  input: {
    message?: string;
    stage?: "scenario" | "record" | "pair";
    sessionId: string;
    sourceRiskArtifactId?: string;
    sourceTbmScenarioArtifactId?: string;
    userLanguage: string;
    modelMode?: AgentModelMode;
    reasoningStyle: AppReasoningStyle;
    deviceContext?: DeviceContextSnapshot;
  },
  signal?: AbortSignal,
) {
  const payload: TbmAgentRunPayload = {
    message: input.message?.trim() || undefined,
    stage: input.stage ?? "pair",
    sessionId: input.sessionId,
    sourceRiskArtifactId: input.sourceRiskArtifactId,
    sourceTbmScenarioArtifactId: input.sourceTbmScenarioArtifactId,
    context: {
      userLanguage: input.userLanguage,
      modelMode: input.modelMode ?? "fast",
      protocol: "v2",
      reasoningStyle: input.reasoningStyle,
      deviceContext: input.deviceContext,
    },
    clientRequestId: crypto.randomUUID(),
  };
  const response = await fetchWithAuth(`${apiUrl()}/api/agents/tbm/runs`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal,
    skipRedirectOn401: false,
  });
  const body = await response.json().catch(() => null);
  if (!response.ok)
    throw new Error(
      apiErrorMessage(
        body,
        `TBM 생성을 시작하지 못했습니다. (${response.status})`,
      ),
    );
  const data = unwrapApiData<unknown>(body);
  const runId =
    data && typeof data === "object"
      ? (data as Record<string, unknown>).runId
      : undefined;
  if (typeof runId !== "string" || !runId)
    throw new Error("Core returned an invalid TBM run response");
  return runId;
}

export function openAgentRunEvents(
  runId: string,
  after: number,
  signal?: AbortSignal,
) {
  return fetchWithAuth(
    `${apiUrl()}/api/agents/runs/${runId}/events?after=${after}`,
    {
      method: "GET",
      signal,
      skipRedirectOn401: false,
    },
  );
}

export function cancelAgentRun(runId: string) {
  return fetchWithAuth(`${apiUrl()}/api/agents/runs/${runId}/cancel`, {
    method: "POST",
    skipRedirectOn401: false,
  });
}

export async function loadAgentSessionMessages(
  sessionId: string,
): Promise<AgentSessionMessage[]> {
  const messages: AgentSessionMessage[] = [];
  const limit = 50;
  let page = 1;
  while (true) {
    const response = await fetchWithAuth(
      `${apiUrl()}/api/chat/sessions/${sessionId}/messages?page=${page}&limit=${limit}`,
      {
        skipRedirectOn401: false,
      },
    );
    const payload = await response.json().catch(() => null);
    if (!response.ok)
      throw new Error(
        apiErrorMessage(payload, `세션 로딩 실패 (${response.status})`),
      );
    const data = unwrapApiData<unknown>(payload);
    if (Array.isArray(data)) {
      messages.push(...(data as AgentSessionMessage[]));
      break;
    }
    if (!data || typeof data !== "object") break;
    const record = data as Record<string, unknown>;
    const values = Array.isArray(record.data)
      ? record.data
      : Array.isArray(record.items)
        ? record.items
        : [];
    messages.push(...(values as AgentSessionMessage[]));
    const total = Number(record.total ?? messages.length);
    if (values.length < limit || messages.length >= total) break;
    page += 1;
  }
  return messages;
}

export async function listAgentSessions(
  page = 1,
  limit = 50,
  surface?: AgentSessionSurface,
) {
  const query = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (surface) query.set("surface", surface);
  const response = await fetchWithAuth(
    `${apiUrl()}/api/chat/sessions?${query.toString()}`,
    {
      skipRedirectOn401: true,
    },
  );
  const payload = await response.json().catch(() => null);
  if (!response.ok)
    throw new Error(
      apiErrorMessage(payload, `세션 목록 로딩 실패 (${response.status})`),
    );
  const data = unwrapApiData<unknown>(payload);
  const record =
    data && typeof data === "object" ? (data as Record<string, unknown>) : {};
  const sessions = Array.isArray(record.sessions)
    ? (record.sessions as AgentSessionListItem[])
    : [];
  const pagination =
    record.pagination && typeof record.pagination === "object"
      ? (record.pagination as Record<string, unknown>)
      : {};
  return {
    sessions,
    page: Number(pagination.page ?? page),
    totalPages: Number(pagination.totalPages ?? page),
  };
}

export async function loadAgentSession(
  sessionId: string,
): Promise<AgentSessionListItem> {
  const response = await fetchWithAuth(
    `${apiUrl()}/api/chat/sessions/${sessionId}`,
    {
      skipRedirectOn401: false,
    },
  );
  const payload = await response.json().catch(() => null);
  if (!response.ok)
    throw new Error(
      apiErrorMessage(payload, `세션 로딩 실패 (${response.status})`),
    );
  const data = unwrapApiData<unknown>(payload);
  const record =
    data && typeof data === "object" ? (data as Record<string, unknown>) : {};
  const nested =
    record.session && typeof record.session === "object"
      ? (record.session as Record<string, unknown>)
      : record;
  if (!isPublicSessionId(nested.id))
    throw new Error("세션 조회 응답에 유효한 공개 ID가 없습니다.");
  return nested as AgentSessionListItem;
}

export async function updateAgentSession(
  sessionId: string,
  body: Record<string, unknown>,
) {
  const response = await fetchWithAuth(
    `${apiUrl()}/api/chat/sessions/${sessionId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      skipRedirectOn401: false,
    },
  );
  const payload = await response.json().catch(() => null);
  if (!response.ok)
    throw new Error(
      apiErrorMessage(payload, `세션 수정 실패 (${response.status})`),
    );
  return unwrapApiData<unknown>(payload);
}
